import { useState, useEffect } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  KEY_DOWN_COMMAND,
  COMMAND_PRIORITY_LOW,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical';
import {
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListItemNode,
  $isListNode,
} from '@lexical/list';
import { useRichTextEditor } from '../RichTextEditorProvider';
import { IconButton, Tooltip } from '@sweep-io/sweep-design';
import { ListNumbers } from '@sweep-io/sweep-design/dist/icons';

export const OrderedListButton = () => {
  const { editor } = useRichTextEditor();
  const [isOrderedList, setIsOrderedList] = useState(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }: any) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();

          if ($isListNode(element)) {
            setIsOrderedList(element.getListType() === 'number');
          } else if ($isListItemNode(element)) {
            const parent = element.getParent();
            setIsOrderedList($isListNode(parent) && parent.getListType() === 'number');
          } else {
            setIsOrderedList(false);
          }
        }
      });
    });
  }, [editor]);

  // Register Tab and Shift+Tab keyboard handlers for list indentation
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.key === 'Tab' && isOrderedList) {
          event.preventDefault();

          if (event.shiftKey) {
            editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
          }

          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, isOrderedList]);

  return (
    <Tooltip title="Numbered List">
      <IconButton
        variant="squared"
        size="small"
        onClick={() => {
          if (isOrderedList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          }
        }}
        pressed={isOrderedList}
      >
        <ListNumbers />
      </IconButton>
    </Tooltip>
  );
};
